/***
*
*   PLAN EDITOR
*   Modal for creating/editing plans with features and add-ons
*
**********/

import { useState, useEffect } from 'react';
import { Button, Icon } from 'components/lib';
import { FeatureEditor } from './FeatureEditor';
import { AddOnsEditor } from './AddOnsEditor';
import Style from './bundlePackaging.tailwind.js';

export function PlanEditor({ plan, isNew, availableFeatures, availableAddOns, onSave, onCancel, loading }) {
  const [formData, setFormData] = useState({
    id: '',
    product_id: '',
    name: '',
    price: 0,
    discounted_price: null,
    discount_percent: null,
    interval: 'month',
    trial_days: null,
    type: 'shown',
    currency: { name: 'usd', symbol: '$' },
    features: {},
    modules_enabled: [],
    add_ons_available: [],
    metadata: {},
    pricing: {
      base: {
        price_id: '',
        amount: 0,
        lookup_key: ''
      },
      per_user: {
        price_id: '',
        amount: 0,
        lookup_key: '',
        base_price_id: ''
      }
    }
  });

  const [activeSection, setActiveSection] = useState('basic');

  useEffect(() => {
    if (plan) {
      setFormData({
        id: plan.id || '',
        product_id: plan.product_id || '',
        name: plan.name || '',
        price: plan.price ?? 0,
        discounted_price: plan.discounted_price ?? null,
        discount_percent: plan.discount_percent ?? null,
        interval: plan.interval || 'month',
        trial_days: plan.trial_days ?? null,
        type: plan.type || 'shown',
        currency: plan.currency || { name: 'usd', symbol: '$' },
        features: plan.features || {},
        modules_enabled: plan.modules_enabled || [],
        add_ons_available: plan.add_ons_available || [],
        metadata: plan.metadata || {},
        pricing: plan.pricing || {
          base: {
            price_id: '',
            amount: plan.price ?? 0,
            lookup_key: ''
          },
          per_user: {
            price_id: '',
            amount: 0,
            lookup_key: '',
            base_price_id: ''
          }
        }
      });
    } else {
      setFormData({
        id: '',
        product_id: '',
        name: '',
        price: 0,
        discounted_price: null,
        discount_percent: null,
        interval: 'month',
        trial_days: null,
        type: 'shown',
        currency: { name: 'usd', symbol: '$' },
        features: {},
        modules_enabled: [],
        add_ons_available: [],
        metadata: {},
        pricing: {
          base: {
            price_id: '',
            amount: 0,
            lookup_key: ''
          },
          per_user: {
            price_id: '',
            amount: 0,
            lookup_key: '',
            base_price_id: ''
          }
        }
      });
    }
  }, [plan]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePricingChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [section]: {
          ...prev.pricing[section],
          [field]: value
        }
      }
    }));
  };

  const handleFeatureChange = (features) => {
    setFormData(prev => ({
      ...prev,
      features,
      modules_enabled: Object.keys(features)
    }));
  };

  const handleAddOnsChange = (addOns, updatedFeatures) => {
    setFormData(prev => ({
      ...prev,
      add_ons_available: addOns,
      features: updatedFeatures || prev.features,
      modules_enabled: updatedFeatures ? Object.keys(updatedFeatures) : prev.modules_enabled
    }));
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    // Sync pricing data to top-level fields for backward compatibility
    const submitData = {
      ...formData,
      price: formData.pricing.base.amount,
      metadata: {
        ...formData.metadata,
        base_price_id: formData.pricing.base.price_id,
        discount_percent: formData.discount_percent?.toString() || '0'
      }
    };

    onSave(submitData);
  };

  const sections = [
    { id: 'basic', label: 'Basic Info', icon: 'info' },
    { id: 'pricing', label: 'Pricing', icon: 'dollar-sign' },
    { id: 'features', label: 'Features', icon: 'list' },
    { id: 'addons', label: 'Add-ons', icon: 'package' },
  ];

  const isSystemPlan = plan?.id === 'free' || plan?.id === 'trial';

  return (
    <div className={Style.modalOverlay} onClick={onCancel}>
      <div className={Style.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={Style.modalHeader}>
          <h2 className={Style.modalTitle}>
            {isNew ? 'Create New Plan' : `Edit ${plan?.name}`}
          </h2>
          <Button
            icon="x"
            color="gray"
            size="small"
            action={onCancel}
          />
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-slate-200 px-6 overflow-x-auto bg-white sticky top-0 z-10">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeSection === section.id
                ? 'text-brand-500 border-brand-500'
                : 'text-slate-500 border-transparent hover:text-slate-700'
                }`}
              onClick={() => setActiveSection(section.id)}
            >
              <Icon image={section.icon} size={16} />
              {section.label}
            </button>
          ))}
        </div>

        {/* Body - Scrollable */}
        <form onSubmit={handleSubmit}>
          <div className={`${Style.modalBody} overflow-y-auto max-h-96`}>
            {/* Basic Info Section */}
            {activeSection === 'basic' && (
              <div className="space-y-4">
                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Plan Name *</label>
                  <input
                    type="text"
                    className={Style.formInput}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g., Professional Plan"
                    required
                    disabled={isSystemPlan}
                  />
                  {isSystemPlan && (
                    <p className="text-xs text-orange-500 mt-1">System plan names cannot be changed</p>
                  )}
                </div>

                {plan?.product_id && (
                  <div className={Style.formGroup}>
                    <label className={Style.formLabel}>Product ID</label>
                    <input
                      type="text"
                      className={Style.formInput + " bg-slate-50"}
                      value={formData.product_id}
                      disabled
                    />
                    <p className="text-xs text-slate-500 mt-1">Stripe product identifier (read-only)</p>
                  </div>
                )}

                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Plan Type</label>
                  <select
                    className={Style.formSelect}
                    value={formData.type || 'shown'}
                    onChange={(e) => handleChange('type', e.target.value)}
                  >
                    <option value="shown">Shown</option>
                    <option value="hidden">Hidden</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Hidden plans are not shown to users during signup</p>
                </div>

                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Billing Interval</label>
                  <select
                    className={Style.formSelect}
                    value={formData.interval || 'month'}
                    onChange={(e) => handleChange('interval', e.target.value)}
                    disabled={true}
                  >
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                  </select>
                  <p className="text-xs text-orange-500 mt-1">Interval selection is handled by Stripe.</p>
                </div>

                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Currency</label>
                  <select
                    className={Style.formSelect}
                    value={formData.currency?.name || 'usd'}
                    onChange={(e) => {
                      const currencies = {
                        usd: { name: 'usd', symbol: '$' },
                        eur: { name: 'eur', symbol: '€' },
                        gbp: { name: 'gbp', symbol: '£' },
                        bdt: { name: 'bdt', symbol: '৳' },
                      };
                      handleChange('currency', currencies[e.target.value] || currencies.usd);
                    }}
                    disabled={true}
                  >
                    <option value="usd">USD ($)</option>
                    <option value="eur">EUR (€)</option>
                    <option value="gbp">GBP (£)</option>
                    <option value="bdt">BDT (৳)</option>
                  </select>
                </div>
              </div>
            )}

            {/* Pricing Section */}
            {activeSection === 'pricing' && (
              <div className="space-y-4">
                {/* Base Pricing */}
                <div className="p-3 bg-slate-50 rounded-lg space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Icon image="tag" size={16} />
                    Base Pricing
                  </h3>

                  <div className={Style.formGroup}>
                    <label className={Style.formLabel}>Base Price (per {formData.interval || 'month'})</label>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-medium text-slate-600">{formData.currency?.symbol || '$'}</span>
                      <input
                        type="number"
                        className={Style.formInput}
                        value={formData.pricing.base.amount ?? ''}
                        onChange={(e) => handlePricingChange('base', 'amount', e.target.value ? parseFloat(e.target.value) : 0)}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        disabled={true}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Base subscription cost</p>
                  </div>

                  <div className={Style.formGroup}>
                    <label className={Style.formLabel}>Price ID</label>
                    <input
                      type="text"
                      className={Style.formInput + " bg-slate-50"}
                      value={formData.pricing.base.price_id || ''}
                      disabled
                    />
                    <p className="text-xs text-slate-500 mt-1">Stripe price identifier (read-only)</p>
                  </div>
                </div>

                {/* Per-User Pricing */}
                <div className="p-3 bg-blue-50 rounded-lg space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Icon image="users" size={16} />
                    Per-User Pricing
                  </h3>

                  <div className={Style.formGroup}>
                    <label className={Style.formLabel}>Per User Price</label>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-medium text-slate-600">{formData.currency?.symbol || '$'}</span>
                      <input
                        type="number"
                        className={Style.formInput}
                        value={formData.pricing.per_user.amount ?? ''}
                        onChange={(e) => handlePricingChange('per_user', 'amount', e.target.value ? parseFloat(e.target.value) : 0)}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        disabled={true}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Cost per additional user</p>
                  </div>

                  <div className={Style.formGroup}>
                    <label className={Style.formLabel}>Per User Price ID</label>
                    <input
                      type="text"
                      className={Style.formInput + " bg-slate-50"}
                      value={formData.pricing.per_user.price_id || ''}
                      disabled
                    />
                    <p className="text-xs text-slate-500 mt-1">Stripe price identifier (read-only)</p>
                  </div>
                </div>

                {/* Discount */}
                <div className="p-3 bg-green-50 rounded-lg space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Icon image="percent" size={16} />
                    Discount
                  </h3>

                  <div className={Style.formGroup}>
                    <label className={Style.formLabel}>Discount Percent (%)</label>
                    <input
                      type="number"
                      className={Style.formInput}
                      value={formData.discount_percent ?? ''}
                      onChange={(e) => handleChange('discount_percent', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="0"
                      min="0"
                      max="100"
                      disabled={true}
                    />
                    <p className="text-xs text-slate-500 mt-1">Overall discount percentage applied to the plan</p>
                  </div>
                </div>

                {/* Trial Settings */}
                {isSystemPlan && (
                  <div className="p-3 bg-yellow-50 rounded-lg space-y-3">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Icon image="clock" size={16} />
                      Trial Settings
                    </h3>

                    <div className={Style.formGroup}>
                      <label className={Style.formLabel}>Trial Days</label>
                      <input
                        type="number"
                        className={Style.formInput}
                        value={formData.trial_days ?? ''}
                        onChange={(e) => handleChange('trial_days', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="14"
                        min="0"
                      />
                      <p className="text-xs text-slate-500 mt-1">Number of trial days before billing starts</p>
                    </div>
                  </div>
                )}

                {/* Pricing Summary */}
                <div className="p-3 bg-slate-100 rounded-lg">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Summary</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Base:</span>
                      <span className="font-medium">{formData.currency?.symbol || '$'}{formData.pricing.base.amount || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Per User:</span>
                      <span className="font-medium">{formData.currency?.symbol || '$'}{formData.pricing.per_user.amount || 0}</span>
                    </div>
                    {formData.discount_percent && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Discount:</span>
                        <span className="font-medium text-green-600">{formData.discount_percent}%</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Features Section */}
            {activeSection === 'features' && (
              <FeatureEditor
                features={formData.features}
                availableFeatures={availableFeatures}
                onChange={handleFeatureChange}
              />
            )}

            {/* Add-ons Section */}
            {activeSection === 'addons' && (
              <AddOnsEditor
                selectedAddOns={formData.add_ons_available}
                availableAddOns={availableAddOns}
                planFeatures={formData.features}
                onChange={handleAddOnsChange}
              />
            )}
          </div>

          {/* Footer */}
          <div className={Style.modalFooter}>
            <Button
              text="Cancel"
              color="gray"
              outline
              action={onCancel}
            />
            <Button
              text={isNew ? 'Create Plan' : 'Save Changes'}
              color="green"
              loading={loading}
              action={handleSubmit}
            />
          </div>
        </form>
      </div>
    </div>
  );
}